import useFormat from "hooks/useFormat";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import ColorLegend from "../charts/lib/ColorLegend";
import { ChartGrid } from "../charts-motion";
import { useColorScaleVDP, VerticalDotPlot } from "../charts-motion/vertical-dot-plot";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_equity_fig_WLGPI";
import { datasets } from "../data/data_equity_fig_WLGPI_datasets";
import { countryIdIso, getCountryMeta, getLevelACName, LevelAC, mkGemEntityDecoder, ordLevelAC, ordRegionCategory, regionIdEq, useButtonGroupState, useCountryEntities, useFigureControlItems, useFigureState, useNamedEntities, useRegionEntities, useSingleSelectState } from "../domain";
import { useMedia } from "../hooks";
import { extentNumber, insertionOrderSet, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, identity, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_equity_fig_WLGPI";
const DEFAULT_COUNTRY = "BFA";
const DEFAULT_CATEGORY = "Sex";
const yUnitLabel = defineMessage({
    id: "fig.equity.WLGPI.yUnitLabel",
    defaultMessage: "Parity index"
});
const Category = io.keyof({
    Location: null,
    Sex: null,
    Wealth: null
});
const categoryLookup = {
    Location: defineMessage({
        id: "fig.equity.WLGPI.Location",
        defaultMessage: "Location"
    }),
    Sex: defineMessage({
        id: "fig.equity.WLGPI.Sex",
        defaultMessage: "Sex"
    }),
    Wealth: defineMessage({
        id: "fig.equity.WLGPI.Wealth",
        defaultMessage: "Wealth"
    })
};
export const Data = mkGemEntityDecoder([], {
    ind_id: LevelAC,
    value: io.number,
    category: Category
});
export const Chart = ({ data: dataRaw })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedCountry] = useSingleSelectState("country");
    const [selectedCategory] = useButtonGroupState("category");
    const entityData_ = useNamedEntities(dataRaw);
    const entityData = React.useMemo(()=>{
        return entityData_.map((x)=>({
                ...x,
                category_name: intl.formatMessage(categoryLookup[x.category]),
                ind_name: getLevelACName(intl, x.ind_id)
            }));
    }, [
        entityData_,
        intl
    ]);
    const countryData = useCountryEntities(entityData);
    const regionData = useRegionEntities(entityData);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    const categoryControlItems = useFigureControlItems(entityData, (x)=>x.category, (x)=>x.category_name);
    const selectedCountryMeta = React.useMemo(()=>{
        return pipe(selectedCountry, O.chain((id)=>getCountryMeta(env.countries, countryIdIso.wrap(id))));
    }, [
        env.countries,
        selectedCountry
    ]);
    React.useEffect(()=>{
        actions.setSelectionControl("category", {
            type: "ButtonGroup",
            selected: DEFAULT_CATEGORY
        }, categoryControlItems);
        actions.setSelectionControl("country", {
            type: "SingleSelect",
            selected: pipe(countryControlItems, Ar.findFirst((x)=>x.value === DEFAULT_COUNTRY), O.alt(()=>Ar.head(countryControlItems)), O.map((x)=>x.value))
        }, countryControlItems);
    }, [
        actions,
        categoryControlItems,
        countryControlItems
    ]);
    const { chartData, colorDomain } = React.useMemo(()=>{
        const countries = pipe(countryData, Ar.filter(({ id })=>pipe(selectedCountry, O.fold(()=>false, (x)=>x === countryIdIso.unwrap(id)))), Ar.sortBy([
            Ord.contramap((x)=>x.entity_name)(Ord.ordString)
        ]));
        const regions = pipe(regionData, Ar.filter(({ id, entity_type })=>entity_type === "world" || pipe(selectedCountryMeta, O.fold(()=>false, (x)=>entity_type === "geo" && regionIdEq.equals(id, x.sdg_region) || entity_type === "income" && x.income != null && regionIdEq.equals(id, x.income)))), Ar.sortBy([
            Ord.contramap((x)=>x.entity_type)(ordRegionCategory)
        ]));
        const data = [
            ...countries,
            ...regions
        ];
        const colorDomain = insertionOrderSet(unsafeFromArray(data), (x)=>x.entity_name);
        return {
            chartData: unsafeFromArray(Ar.sortBy([
                Ord.contramap((x)=>x.ind_id)(ordLevelAC),
                Ord.contramap((x)=>x.entity_type)(ordRegionCategory),
                Ord.contramap((x)=>x.entity_name)(Ord.ordString)
            ])(data.filter(({ category })=>category === selectedCategory))),
            colorDomain
        };
    }, [
        countryData,
        regionData,
        selectedCountry,
        selectedCategory,
        selectedCountryMeta
    ]);
    const getX = React.useCallback((x)=>x.ind_name, []);
    const getValue = React.useCallback((x)=>x.value, []);
    const getColor = React.useCallback((x)=>x.entity_name, []);
    const getColorPalette = React.useCallback(()=>M.colorRanges.discrete, []);
    const formatValue = useFormat(".2f");
    const formatAxisValue = useFormat(".1f");
    const extentAll = React.useMemo(()=>{
        return extentNumber(unsafeFromArray(entityData), getValue);
    }, [
        entityData,
        getValue
    ]);
    const [colorScale, colorLegendValues] = useColorScaleVDP({
        data: chartData,
        getColor,
        colorPalette: getColorPalette,
        formatColor: identity,
        domainColor: colorDomain
    });
    return <>
      <ChartGrid data={chartData} getCell={getX} columnCount={5} minWidth={screenMDown ? 60 : 80}>
        {({ data, width, firstInRow })=><VerticalDotPlot width={width} height={screenSDown ? M.chartHeight.xs : screenMDown ? M.chartHeight.s : M.chartHeight.m} data={data} getX={getX} getValue={getValue} getColor={getColor} formatX={identity} formatValue={formatValue} formatAxisValue={formatAxisValue} domainY={extentAll} colorScale={colorScale} colorLegendValues={colorLegendValues} showTooltip labelStyleX="none" marginLeft={20} hideYAxis={!firstInRow} yUnitLabel={firstInRow ? intl.formatMessage(yUnitLabel) : undefined} yAnnotations={[
            {
                type: "band",
                label: intl.formatMessage(defineMessage({
                    id: "Parity",
                    defaultMessage: "Parity"
                })),
                range: [
                    0.95,
                    1.05
                ],
                dy: screenMDown ? "-0.7em" : undefined
            },
            {
                type: "line",
                value: 1
            }
        ]} dotStyle={screenSDown ? "medium" : "default"}/>}
      </ChartGrid>
      <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>
    </>;
};
export default withFigureIO({
    url: new URL("../data/data_equity_fig_WLGPI.json", import.meta.url).href,
    csv: new URL("../data/data_equity_fig_WLGPI.zip", import.meta.url).href,
    xlsx: new URL("../data/data_equity_fig_WLGPI.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "wide",
    datasets
});
